import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import { useNavbarUser } from '../../utils/navbarUser';
import { authApi, SPADES_UPDATED_EVENT } from '../../services/authApi';
import streamApi from '../../services/streamApi';
import PlatformPlanSection from './PlatformPlanSection';
import './SubscriptionsPage.css';

const SUBSCRIPTION_COST = 2500;

const SpadeIcon = () => (
  <svg width="18" height="21" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
    <path d="M13.4618 16.6661L14.9018 26.5661L8.3818 26.5461C9.0518 23.2661 9.72181 19.9761 10.3918 16.6861C10.0518 17.1161 9.41181 17.8061 8.39181 18.3061C7.81181 18.5961 6.21171 19.2661 4.29171 18.7561C3.91171 18.6561 1.6117 18.0461 0.781702 15.9561C0.241702 14.5961 0.611712 13.3461 0.791712 12.7461C1.10171 11.7461 1.58176 11.1261 3.71176 8.93609C5.80176 6.78609 7.00172 5.55609 8.55172 4.12609C8.55172 4.12609 10.1818 2.64609 11.2418 1.45609C11.2418 1.44609 11.8818 0.746094 11.8818 0.746094C12.5518 1.49609 13.4418 2.48609 14.5018 3.62609C15.4618 4.65609 16.7418 6.01609 18.4618 7.72609C19.3518 8.60609 19.9418 9.17609 20.8518 10.1861C21.1618 10.5261 21.5718 10.9061 22.0018 11.5261C22.1018 11.6661 22.4018 12.0861 22.5818 12.4461C23.2618 13.8361 22.6617 15.4661 22.5517 15.7461C22.4017 16.1561 21.7618 17.7961 19.9618 18.5861C18.5018 19.2261 17.1618 18.9361 16.7418 18.8361C16.2518 18.6961 15.5517 18.4261 14.8217 17.9361C14.1817 17.5061 13.7317 17.0361 13.4317 16.6761L13.4618 16.6661Z" fill="currentColor" stroke="currentColor" strokeMiterlimit="10" strokeWidth="0.5"/>
  </svg>
);

const PerkIcon = ({ children }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const SPADES_PERKS = [
  {
    icon: (
      <PerkIcon>
        <circle cx="12" cy="8" r="6" />
        <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
      </PerkIcon>
    ),
    title: 'Exclusive Badge',
    desc: 'Flex a subscriber badge next to your name and everyone will notice.',
  },
  {
    icon: (
      <PerkIcon>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </PerkIcon>
    ),
    title: 'Priority Chat',
    desc: 'Your messages rise to the top so the streamer always sees you.',
  },
  {
    icon: (
      <PerkIcon>
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
        <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
      </PerkIcon>
    ),
    title: 'First in Line',
    desc: "Your images and GIFs play before anyone else's, every time.",
  },
];

function SubscriptionsPage() {
  const user = useNavbarUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [planBanner, setPlanBanner] = useState(null);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [mySubscribers, setMySubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingFor, setCancellingFor] = useState('');
  const searchTimer = useRef(null);
  const activationRan = useRef(false);

  const myUserId = user?.id || null;

  const refresh = async () => {
    setLoading(true);
    try {
      const [subs, subers] = await Promise.all([
        streamApi.getMySubscriptions().catch(() => []),
        streamApi.getMySubscribers().catch(() => []),
      ]);
      setMySubscriptions(subs);
      setMySubscribers(subers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (activationRan.current) return;
    const params = new URLSearchParams(location.search);
    const plan = params.get('plan');
    const sessionId = params.get('session_id');
    if (!plan) return;
    activationRan.current = true;

    if (plan === 'success' && sessionId) {
      authApi
        .activatePlatformPlan(sessionId)
        .then(() => {
          setPlanBanner({ type: 'success', message: 'Discovery Plan activated — welcome to PRO!' });
        })
        .catch((err) => {
          setPlanBanner({
            type: 'error',
            message: err?.message || 'Could not activate plan. Contact support.',
          });
        });
    } else if (plan === 'cancelled') {
      setPlanBanner({ type: 'error', message: 'Checkout cancelled — no charge was made.' });
    }
    navigate({ pathname: location.pathname }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const results = await authApi.searchUsers(trimmed);
        const filtered = Array.isArray(results)
          ? results.filter((u) => u.id && u.id !== myUserId)
          : [];
        setSearchResults(filtered.slice(0, 8));
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [query, myUserId]);

  const activeSubscriptions = useMemo(
    () => mySubscriptions.filter((s) => s.status === 'active'),
    [mySubscriptions],
  );
  const pastSubscriptions = useMemo(
    () => mySubscriptions.filter((s) => s.status !== 'active'),
    [mySubscriptions],
  );

  const handleSelect = (candidate) => {
    setSelected(candidate);
    setQuery(candidate.username || '');
    setSearchResults([]);
    setError('');
  };

  const handleSubscribe = async () => {
    if (!selected?.id) {
      setError('Pick a streamer to subscribe to');
      return;
    }
    setSubscribing(true);
    setError('');
    setSuccess('');
    try {
      const result = await streamApi.subscribeToStreamer(selected.id);
      setSuccess(`Subscribed to ${selected.username}`);
      setSelected(null);
      setQuery('');
      if (typeof result?.new_balance === 'number' && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(SPADES_UPDATED_EVENT, {
            detail: { spadeBalance: result.new_balance },
          }),
        );
      }
      await refresh();
    } catch (err) {
      setError(err?.message || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async (streamerUserId) => {
    if (!streamerUserId) return;
    setCancellingFor(streamerUserId);
    setError('');
    setSuccess('');
    try {
      await streamApi.cancelSubscription(streamerUserId);
      setSuccess('Subscription cancelled');
      await refresh();
    } catch (err) {
      setError(err?.message || 'Failed to cancel subscription');
    } finally {
      setCancellingFor('');
    }
  };

  return (
    <div className="subscriptions-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={user.notifications}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onLogout={() => {}}
      />
      <LeftSidebar />
      <main className="subscriptions-page__main">
        <div className="subscriptions-page__content">
          <section className="subscriptions-page__hero">
            <div className="subscriptions-page__hero-copy">
              <p className="subscriptions-page__eyebrow">Support and discovery</p>
              <h1 className="subscriptions-page__title">Subscriptions</h1>
              <p className="subscriptions-page__intro">
                Manage streamer support, Discovery, subscribers, and renewal status from one place.
              </p>
            </div>
            <div className="subscriptions-page__hero-stats" aria-label="Subscription summary">
              <div className="subscriptions-page__hero-stat">
                <span>Active</span>
                <strong>{loading ? '—' : activeSubscriptions.length}</strong>
              </div>
              <div className="subscriptions-page__hero-stat">
                <span>Subscribers</span>
                <strong>{loading ? '—' : mySubscribers.length}</strong>
              </div>
              <div className="subscriptions-page__hero-stat subscriptions-page__hero-stat--spend">
                <span>Monthly spend</span>
                <strong>
                  {loading ? '—' : (
                    <>
                      {(activeSubscriptions.length * SUBSCRIPTION_COST).toLocaleString()}
                      <SpadeIcon />
                    </>
                  )}
                </strong>
              </div>
            </div>
          </section>

          {error && <div className="subscriptions-page__alert subscriptions-page__alert--error">{error}</div>}
          {success && <div className="subscriptions-page__alert subscriptions-page__alert--success">{success}</div>}

          <div className="subscriptions-page__cards-row">
            <section className="subscriptions-page__card subscriptions-page__card--spades">
              <div className="subscriptions-page__pro-header">
                <h2 className="subscriptions-page__pro-title">
                  <span className="subscriptions-page__pro-star" aria-hidden="true">♠</span>
                  Subscribe with spades
                </h2>
                <span className="subscriptions-page__pro-price">
                  {SUBSCRIPTION_COST.toLocaleString()} spades / 30 days
                </span>
              </div>
              <p className="subscriptions-page__pro-subtitle">
                Support a specific streamer each month. Your spades go straight to them.
              </p>
              <div className="subscriptions-page__pro-perks">
                <p className="subscriptions-page__pro-perks-label">What you get</p>
                <div className="subscriptions-page__pro-perks-grid">
                  {SPADES_PERKS.map((perk) => (
                    <div key={perk.title} className="subscriptions-page__pro-perk">
                      <span className="subscriptions-page__pro-perk-icon">{perk.icon}</span>
                      <div>
                        <p className="subscriptions-page__pro-perk-title">{perk.title}</p>
                        <p className="subscriptions-page__pro-perk-desc">{perk.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="subscriptions-page__search">
                <input
                  type="text"
                  className="subscriptions-page__input"
                  placeholder="Search streamer username"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(null);
                  }}
                  autoComplete="off"
                />
                {searchResults.length > 0 && (
                  <ul className="subscriptions-page__results">
                    {searchResults.map((match) => (
                      <li key={match.id}>
                        <button
                          type="button"
                          className="subscriptions-page__result"
                          onClick={() => handleSelect(match)}
                        >
                          <UserAvatar
                            username={match.username}
                            avatarUrl={match.profileImage}
                          />
                          <span>{match.username}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className="subscriptions-page__primary"
                onClick={handleSubscribe}
                disabled={!selected || subscribing}
              >
                {subscribing
                  ? 'Subscribing…'
                  : selected
                    ? `Subscribe to @${selected.username} (${SUBSCRIPTION_COST.toLocaleString()} spades)`
                    : 'Pick a streamer'}
              </button>
            </section>

            <PlatformPlanSection
              banner={planBanner}
              onBannerClear={() => setPlanBanner(null)}
            />
          </div>

          <div className="subscriptions-page__management-grid">
            <section className="subscriptions-page__card">
              <div className="subscriptions-page__section-heading">
                <div>
                  <p className="subscriptions-page__section-kicker">Support</p>
                  <h2 className="subscriptions-page__section-title">My subscriptions</h2>
                </div>
                {!loading && activeSubscriptions.length > 0 && (
                  <span className="subscriptions-page__section-count">
                    {activeSubscriptions.length} active
                  </span>
                )}
              </div>
              {!loading && activeSubscriptions.length > 0 && (
                <p className="subscriptions-page__spend">
                  You&apos;re spending{' '}
                  <strong>
                    {(activeSubscriptions.length * SUBSCRIPTION_COST).toLocaleString()} spades/month
                  </strong>{' '}
                  on {activeSubscriptions.length} subscription{activeSubscriptions.length === 1 ? '' : 's'}.
                </p>
              )}
              {loading ? (
                <p className="subscriptions-page__muted">Loading…</p>
              ) : activeSubscriptions.length === 0 ? (
                <div className="subscriptions-page__empty">
                  <strong>Your supporter list is empty.</strong>
                  <span>Search for a streamer above and subscribe with spades when you want to support them.</span>
                </div>
              ) : (
                <ul className="subscriptions-page__list">
                  {activeSubscriptions.map((sub) => (
                    <li key={sub.streamer_user_id} className="subscriptions-page__row">
                      <div className="subscriptions-page__row-identity">
                        <UserAvatar
                          username={sub.streamer_username}
                          avatarUrl={sub.streamer_avatar_url}
                        />
                        <div>
                          <UsernameLink username={sub.streamer_username} />
                          <div className="subscriptions-page__row-meta">
                            {renewalLabel(sub.next_renewal_at)}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="subscriptions-page__secondary"
                        onClick={() => handleCancel(sub.streamer_user_id)}
                        disabled={cancellingFor === sub.streamer_user_id}
                      >
                        {cancellingFor === sub.streamer_user_id ? 'Cancelling…' : 'Cancel'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {pastSubscriptions.length > 0 && (
                <details className="subscriptions-page__past">
                  <summary>Past subscriptions ({pastSubscriptions.length})</summary>
                  <ul className="subscriptions-page__list">
                    {pastSubscriptions.map((sub) => (
                      <li key={`${sub.streamer_user_id}-${sub.created_at}`} className="subscriptions-page__row">
                        <div className="subscriptions-page__row-identity">
                          <UserAvatar
                            username={sub.streamer_username}
                            avatarUrl={sub.streamer_avatar_url}
                          />
                          <div>
                            <UsernameLink username={sub.streamer_username} />
                            <div className="subscriptions-page__row-meta">
                              {sub.status} · started {formatDate(sub.created_at)}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            <section className="subscriptions-page__card">
              <div className="subscriptions-page__section-heading">
                <div>
                  <p className="subscriptions-page__section-kicker">Audience</p>
                  <h2 className="subscriptions-page__section-title">
                    My subscribers{!loading && mySubscribers.length > 0 ? ` (${mySubscribers.length})` : ''}
                  </h2>
                </div>
              </div>
              {loading ? (
                <p className="subscriptions-page__muted">Loading…</p>
              ) : mySubscribers.length === 0 ? (
                <div className="subscriptions-page__empty">
                  <strong>No active subscribers yet.</strong>
                  <span>Share your profile or go live to start converting fans into monthly supporters.</span>
                </div>
              ) : (
                <ul className="subscriptions-page__list">
                  {mySubscribers.map((sub) => (
                    <li key={sub.viewer_user_id} className="subscriptions-page__row">
                      <div className="subscriptions-page__row-identity">
                        <UserAvatar
                          username={sub.viewer_username}
                          avatarUrl={sub.viewer_avatar_url}
                        />
                        <div>
                          <UsernameLink username={sub.viewer_username} />
                          <div className="subscriptions-page__row-meta">
                            Since {formatDate(sub.created_at)}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '—';
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

function renewalLabel(iso) {
  const days = daysUntil(iso);
  if (days === null) return `Renews on ${formatDate(iso)}`;
  if (days <= 0) return 'Renews today';
  if (days === 1) return 'Renews in 1 day';
  return `Renews in ${days} days`;
}

function initialsOf(username) {
  const trimmed = String(username || '').trim();
  if (!trimmed) return '?';
  return trimmed.slice(0, 2).toUpperCase();
}

function UserAvatar({ username, avatarUrl }) {
  if (avatarUrl) {
    return (
      <img
        className="subscriptions-page__avatar"
        src={avatarUrl}
        alt={username || 'user'}
        loading="lazy"
      />
    );
  }
  return (
    <div className="subscriptions-page__avatar subscriptions-page__avatar--fallback">
      {initialsOf(username)}
    </div>
  );
}

function UsernameLink({ username }) {
  if (!username) {
    return <div className="subscriptions-page__row-title">—</div>;
  }
  return (
    <Link
      to={`/profile/${encodeURIComponent(username)}`}
      className="subscriptions-page__row-title subscriptions-page__row-link"
    >
      {username}
    </Link>
  );
}

export default SubscriptionsPage;
